import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { Card, CardProps } from '../Card';
import { HeadingDivider } from '../HeadingDivider/HeadingDivider';
import { styles } from './CardGroup.css';

export const CARD_MIN_WIDTH_PX = 350;
export const CARD_CAROUSEL_WIDTH_PX = 345;
export const CARD_GAP_PX = 10;
export const MAX_CARDS_PER_ROW = 4;

export const getCardsPerRow = (containerWidth?: number) => {
  if (!containerWidth || containerWidth <= 0) {
    return 1;
  }

  return Math.max(
    1,
    Math.min(MAX_CARDS_PER_ROW, Math.floor((containerWidth + CARD_GAP_PX) / (CARD_MIN_WIDTH_PX + CARD_GAP_PX)))
  );
};

export const getCardsDisplayCount = ({
  desktopRows,
  cardsPerRow,
  totalCards,
}: {
  desktopRows?: number;
  cardsPerRow: number;
  totalCards: number;
}) => {
  if (desktopRows === undefined) {
    return totalCards;
  }

  return Math.min(totalCards, Math.max(desktopRows, 0) * cardsPerRow);
};

export interface CardGroupProps {
  heading?: string;
  cards: Array<CardProps>;
  desktopRows?: number;
  linkProps?: {
    href: string;
    label: string;
  };
  enableContainerBreakpoints?: boolean;
}

export const CardGroup = ({
  heading,
  cards,
  desktopRows,
  linkProps,
  enableContainerBreakpoints = false,
}: CardGroupProps) => {
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const breakpointRef = enableContainerBreakpoints ? cardsContainerRef : undefined;
  const isDesktop = useBreakpoint('md', 'up', breakpointRef);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(() =>
    typeof window !== 'undefined' ? window.innerWidth : undefined
  );
  const cardsPerRow = getCardsPerRow(containerWidth);
  const visibleCards = !isDesktop
    ? cards
    : cards.slice(0, getCardsDisplayCount({ desktopRows, cardsPerRow, totalCards: cards.length }));

  useEffect(() => {
    const updateContainerWidth = () => {
      const nextWidth = cardsContainerRef.current?.clientWidth;
      setContainerWidth(nextWidth || (typeof window !== 'undefined' ? window.innerWidth : undefined));
    };

    updateContainerWidth();

    if (!cardsContainerRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateContainerWidth();
    });

    resizeObserver.observe(cardsContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className={styles.container}>
      {heading ? (
        <div className={styles.headingContainer}>
          <div className={styles.heading}>
            <HeadingDivider as="h2" heading={heading} href={linkProps?.href} />
          </div>
        </div>
      ) : null}
      <div className={styles.cardsContainer} ref={cardsContainerRef}>
        {!isDesktop ? (
          <Carousel
            showProgressBar
            spaceBetween={CARD_GAP_PX}
            slidesOffsetAfter={2}
            showPagination={false}
            showArrows={false}
            slidesPerView="auto"
          >
            {visibleCards.map((cardProps, index) => (
              <Card key={index} {...cardProps} width={cardProps.width ?? `${CARD_CAROUSEL_WIDTH_PX}px`} />
            ))}
          </Carousel>
        ) : (
          <div className={styles.cardsGrid}>
            {visibleCards.map((cardProps, index) => (
              <Card key={index} {...cardProps} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
